// SigningPageBuilder — Canva-style multi-page editor for upload-type
// proposals.
//
// Layout:
//   • Left rail: ordered thumbnails of every page (PDF pages, video pages,
//                 signing pages). Reorder via drag; delete / duplicate.
//                 Hover a gap between two tiles to insert a Video or
//                 Signing page.
//   • Center  : the active page, big, with every field placed on it. Drag
//                 fields to reposition; corner handle resizes. Click a
//                 field to open the inspector.
//   • Right rail: top → Field palette (drag to canvas or click to place),
//                 middle → Signers manager (add/edit/remove, pick workflow),
//                 bottom → active-field inspector.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, User, Mail, Building2, IdCard, Calendar, Type, PenLine,
  Trash2, Copy, Eye, EyeOff, ExternalLink, Download, Share2, Save, Loader2,
  Video as VideoIcon, FileText, PlusCircle, UserPlus, GripVertical, Plus,
  AlertCircle, ChevronDown, ChevronRight, Link2, Activity, X, Send, CheckCircle2, Clock, Ban,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmModal";
import { VideoBlockEditor } from "../components/VideoBlock";

const FIELD_PALETTE = [
  { type: "client_name",    icon: User,      label: "Full name",    w: 0.30, h: 0.045, required: true },
  { type: "client_email",   icon: Mail,      label: "Email",        w: 0.30, h: 0.045, required: true },
  { type: "client_company", icon: Building2, label: "Company",      w: 0.30, h: 0.045 },
  { type: "client_title",   icon: IdCard,    label: "Title",        w: 0.30, h: 0.045 },
  { type: "date",           icon: Calendar,  label: "Date",         w: 0.22, h: 0.045 },
  { type: "text",           icon: Type,      label: "Text",         w: 0.40, h: 0.06, value: "Add text or instructions here." },
  { type: "signature",      icon: PenLine,   label: "Signature",    w: 0.45, h: 0.10, required: true },
];
const PALETTE_BY_TYPE = Object.fromEntries(FIELD_PALETTE.map(p => [p.type, p]));

const SIGNER_COLORS = [
  { fill: "rgba(91,79,232,0.14)",  stroke: "#5B4FE8", chip: "#5B4FE8" }, // purple
  { fill: "rgba(14,165,164,0.14)", stroke: "#0EA5A4", chip: "#0EA5A4" }, // teal
  { fill: "rgba(234,88,12,0.14)",  stroke: "#EA580C", chip: "#EA580C" }, // orange
  { fill: "rgba(132,204,22,0.14)", stroke: "#65A30D", chip: "#65A30D" }, // lime
  { fill: "rgba(192,38,211,0.14)", stroke: "#C026D3", chip: "#C026D3" }, // fuchsia
  { fill: "rgba(14,116,144,0.14)", stroke: "#0E7490", chip: "#0E7490" }, // cyan
];
const colorForSigner = (idx) => SIGNER_COLORS[idx % SIGNER_COLORS.length];

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SigningPageBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [docUrl, setDocUrl] = useState(null);
  const [pages, setPages] = useState([]);
  const [signers, setSigners] = useState([]);
  const [workflow, setWorkflow] = useState("sequential");
  const [fields, setFields] = useState([]);
  const [activePageId, setActivePageId] = useState(null);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [activeSignerId, setActiveSignerId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({ pages: false, signers: false, fields: false });

  const canvasRef = useRef(null);
  const dragFieldRef = useRef(null);
  const dragPageRef = useRef(null);
  const dragPaletteRef = useRef(null);

  const markDirty = (key) => setDirty((d) => ({ ...d, [key]: true }));

  // ----- load / save ------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/proposals/${id}/upload-detail`);
      const p = data.proposal;
      setProposal(p);
      setDocUrl(data.doc_url);
      setPages(p.pages || []);
      setSigners(p.signers || []);
      setWorkflow(p.signing_workflow || "sequential");
      setFields(p.fields || []);
      setActivePageId((p.pages && p.pages[0]?.id) || null);
      setActiveSignerId((p.signers && p.signers[0]?.id) || null);
      setDirty({ pages: false, signers: false, fields: false });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load proposal");
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveAll = async () => {
    setSaving(true);
    try {
      if (dirty.pages) {
        await api.put(`/proposals/${id}/pages`, {
          pages: pages.map(p => ({
            id: p.id, type: p.type,
            source_file_id: p.source_file_id, source_index: p.source_index,
            source_meta: p.source_meta,
            label: p.label, bg_color: p.bg_color,
            video: p.video,
          })),
        });
      }
      if (dirty.signers) {
        await api.put(`/proposals/${id}/signers`, {
          signers: signers.map(s => ({ id: s.id, label: s.label, name: s.name, email: s.email, order: s.order })),
          signing_workflow: workflow,
        });
      }
      if (dirty.fields) {
        await api.put(`/proposals/${id}/fields`, { fields });
      }
      toast.success("Saved");
      setDirty({ pages: false, signers: false, fields: false });
      // Reload to pick up server-generated ids
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  const anyDirty = dirty.pages || dirty.signers || dirty.fields;

  const publish = async () => {
    if (anyDirty) await saveAll();
    try {
      await api.post(`/proposals/${id}/publish`);
      // Fire invite emails to signer 1 (sequential) / all signers (parallel).
      try {
        const { data } = await api.post(`/proposals/${id}/send-signer-invites`);
        const sent = (data.sent || []).filter(s => s.status === "sent").length;
        const skipped = (data.sent || []).filter(s => s.status.startsWith("skipped")).length;
        if (sent) toast.success(`Published · emailed ${sent} signer${sent === 1 ? "" : "s"}`);
        else if (skipped) toast.success(`Published — add an email to each signer so Pragma can invite them`);
        else toast.success("Published");
      } catch {
        toast.success("Published (couldn't send invites — check Signer emails)");
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Publish failed"); }
  };
  const unpublish = async () => {
    try {
      await api.post(`/proposals/${id}/unpublish`);
      toast.success("Back to draft");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unpublish failed"); }
  };

  // ----- page operations --------------------------------------------------
  const insertPage = (afterIndex, type) => {
    const base = { id: uid(), type };
    if (type === "video") {
      base.video = { embed_url: "", title: "", intro: "", bg_color: "#5B4FE8", layout: "with_text" };
    } else {
      base.label = "Signatures & Client Details";
    }
    const next = [...pages];
    next.splice(afterIndex + 1, 0, base);
    setPages(next);
    setActivePageId(base.id);
    markDirty("pages");
  };

  const deletePage = async (pageId) => {
    const ok = await confirm({
      title: "Delete page?",
      message: "Any fields on this page will also be removed. This can be undone by not saving.",
      destructive: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    const next = pages.filter(p => p.id !== pageId);
    setPages(next);
    setFields(fs => fs.filter(f => f.page_id !== pageId));
    if (activePageId === pageId) setActivePageId(next[0]?.id || null);
    markDirty("pages");
    markDirty("fields");
  };

  const duplicatePage = (pageId) => {
    const idx = pages.findIndex(p => p.id === pageId);
    if (idx < 0) return;
    const src = pages[idx];
    if (src.type === "pdf_page") {
      // PDF pages reference the source; duplicating keeps same source_index.
      const copy = { ...src, id: uid() };
      const next = [...pages];
      next.splice(idx + 1, 0, copy);
      setPages(next);
      setActivePageId(copy.id);
      markDirty("pages");
      return;
    }
    const copy = { ...src, id: uid() };
    // Duplicate fields on this page for the new page
    const copiedFields = fields.filter(f => f.page_id === pageId).map(f => ({ ...f, id: uid(), page_id: copy.id }));
    const next = [...pages];
    next.splice(idx + 1, 0, copy);
    setPages(next);
    setFields([...fields, ...copiedFields]);
    setActivePageId(copy.id);
    markDirty("pages");
    if (copiedFields.length) markDirty("fields");
  };

  // Page drag reorder (left rail)
  const onPageDragStart = (e, pageId) => {
    dragPageRef.current = pageId;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", pageId);
  };
  const onPageDragOver = (e) => {
    if (dragPageRef.current) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }
  };
  const onPageDrop = (e, targetId) => {
    e.preventDefault();
    const src = dragPageRef.current;
    dragPageRef.current = null;
    if (!src || src === targetId) return;
    const srcIdx = pages.findIndex(p => p.id === src);
    const tgtIdx = pages.findIndex(p => p.id === targetId);
    if (srcIdx < 0 || tgtIdx < 0) return;
    const next = [...pages];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    setPages(next);
    markDirty("pages");
  };

  // ----- field operations -------------------------------------------------
  const activePage = pages.find(p => p.id === activePageId);
  const activeSigner = signers.find(s => s.id === activeSignerId);
  const activeSignerIdx = signers.findIndex(s => s.id === activeSignerId);
  const activeColor = activeSignerIdx >= 0 ? colorForSigner(activeSignerIdx) : SIGNER_COLORS[0];

  const addField = (type, atX = null, atY = null) => {
    if (!activePage) { toast.error("Pick a page first"); return; }
    if (activePage.type === "video") { toast.error("Fields can't be added to a video page"); return; }
    if (!activeSigner) { toast.error("Pick a signer first"); return; }
    const base = PALETTE_BY_TYPE[type];
    if (!base) return;
    // Place at drop point or stacked default
    const existing = fields.filter(f => f.page_id === activePageId).length;
    const nf = {
      id: uid(),
      page_id: activePageId,
      signer_id: activeSignerId,
      type,
      label: base.label,
      value: base.value || null,
      required: !!base.required,
      x: atX !== null ? Math.max(0, Math.min(1 - base.w, atX)) : 0.1,
      y: atY !== null ? Math.max(0, Math.min(1 - base.h, atY)) : Math.min(0.9, 0.08 + existing * 0.07),
      width: base.w,
      height: base.h,
    };
    setFields(f => [...f, nf]);
    setSelectedFieldId(nf.id);
    markDirty("fields");
  };

  const updateField = (fid, patch) => {
    setFields(fs => fs.map(f => f.id === fid ? { ...f, ...patch } : f));
    markDirty("fields");
  };

  const deleteField = (fid) => {
    setFields(fs => fs.filter(f => f.id !== fid));
    if (selectedFieldId === fid) setSelectedFieldId(null);
    markDirty("fields");
  };

  const duplicateField = (fid) => {
    const src = fields.find(f => f.id === fid);
    if (!src) return;
    const copy = {
      ...src,
      id: uid(),
      x: Math.min(1 - src.width, src.x + 0.02),
      y: Math.min(1 - src.height, src.y + 0.04),
    };
    setFields(fs => [...fs, copy]);
    setSelectedFieldId(copy.id);
    markDirty("fields");
  };

  // Pointer drag/resize on the canvas
  const onFieldPointerDown = (e, fid, mode) => {
    e.stopPropagation(); e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const f = fields.find(ff => ff.id === fid);
    if (!f) return;
    setSelectedFieldId(fid);
    dragFieldRef.current = {
      id: fid, mode,
      startX: e.clientX, startY: e.clientY,
      origX: f.x, origY: f.y, origW: f.width, origH: f.height,
      cw: rect.width, ch: rect.height,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };
  const onPointerMove = useCallback((e) => {
    const d = dragFieldRef.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / d.cw;
    const dy = (e.clientY - d.startY) / d.ch;
    setFields(fs => fs.map(f => {
      if (f.id !== d.id) return f;
      if (d.mode === "move") {
        return {
          ...f,
          x: Math.max(0, Math.min(1 - f.width, d.origX + dx)),
          y: Math.max(0, Math.min(1 - f.height, d.origY + dy)),
        };
      }
      return {
        ...f,
        width: Math.max(0.03, Math.min(1 - f.x, d.origW + dx)),
        height: Math.max(0.025, Math.min(1 - f.y, d.origH + dy)),
      };
    }));
    markDirty("fields");
  }, []);
  const onPointerUp = useCallback(() => {
    dragFieldRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove]);

  useEffect(() => () => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove, onPointerUp]);

  // Palette → canvas drag-and-drop (click also works via addField)
  const onPaletteDragStart = (e, type) => {
    dragPaletteRef.current = type;
    e.dataTransfer.effectAllowed = "copy";
  };
  const onCanvasDragOver = (e) => {
    if (dragPaletteRef.current) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  };
  const onCanvasDrop = (e) => {
    const type = dragPaletteRef.current;
    dragPaletteRef.current = null;
    if (!type) return;
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;
    addField(type, nx - 0.05, ny - 0.02);
  };

  // Signer management
  const addSigner = () => {
    const nextOrder = (signers[signers.length - 1]?.order || 0) + 1;
    const s = { id: uid(), label: `Signer ${signers.length + 1}`, name: "", email: "", order: nextOrder, status: "pending", token: null };
    setSigners([...signers, s]);
    setActiveSignerId(s.id);
    markDirty("signers");
  };
  const removeSigner = async (sid) => {
    if (signers.length === 1) { toast.error("At least one signer is required"); return; }
    const ok = await confirm({
      title: "Remove signer?",
      message: "Any fields assigned to this signer will also be removed.",
      destructive: true,
      confirmLabel: "Remove",
    });
    if (!ok) return;
    setSigners(ss => ss.filter(s => s.id !== sid));
    setFields(fs => fs.filter(f => f.signer_id !== sid));
    if (activeSignerId === sid) setActiveSignerId(signers.find(s => s.id !== sid)?.id || null);
    markDirty("signers");
    markDirty("fields");
  };
  const updateSigner = (sid, patch) => {
    setSigners(ss => ss.map(s => s.id === sid ? { ...s, ...patch } : s));
    markDirty("signers");
  };
  const moveSigner = (sid, delta) => {
    const idx = signers.findIndex(s => s.id === sid);
    const tgt = idx + delta;
    if (idx < 0 || tgt < 0 || tgt >= signers.length) return;
    const next = [...signers];
    [next[idx], next[tgt]] = [next[tgt], next[idx]];
    next.forEach((s, i) => s.order = i + 1);
    setSigners(next);
    markDirty("signers");
  };

  const copyShareLink = async (signer) => {
    const url = `${window.location.origin}/p/upload/${signer.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link for ${signer.label} copied`);
    } catch {
      toast.error("Couldn't copy — select the input and copy manually");
    }
  };

  const resendInvites = async () => {
    try {
      const { data } = await api.post(`/proposals/${id}/send-signer-invites`);
      const sent = (data.sent || []).filter(s => s.status === "sent").length;
      const failed = (data.sent || []).filter(s => s.status === "failed").length;
      const skipped = (data.sent || []).filter(s => s.status.startsWith("skipped")).length;
      if (sent) toast.success(`Resent invites to ${sent} signer${sent === 1 ? "" : "s"}`);
      else if (skipped) toast.error("No signer with an email to invite yet");
      else if (failed) toast.error("Could not send invites — check backend logs");
      else toast.success("No invite needed — everyone has signed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resend failed");
    }
  };

  const fieldsBySigner = useMemo(() => {
    const m = {};
    fields.forEach(f => { m[f.signer_id] = (m[f.signer_id] || 0) + 1; });
    return m;
  }, [fields]);

  // Signer activity drawer state
  const [activityOpen, setActivityOpen] = useState(false);
  const [activity, setActivity] = useState(null); // { signers: [{id, label, events: [...]}] }
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFocusId, setActivityFocusId] = useState(null);

  const openActivity = async (focusSignerId = null) => {
    setActivityOpen(true);
    setActivityFocusId(focusSignerId);
    setActivityLoading(true);
    try {
      const { data } = await api.get(`/proposals/${id}/signer-activity`);
      setActivity(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load activity");
    }
    setActivityLoading(false);
  };

  if (loading) return <div className="p-10 text-center text-sm" style={{ color: "var(--pg-text-mute)" }}>Loading…</div>;
  if (!proposal) return <div className="p-10 text-center text-sm">Proposal not found.</div>;

  const isPublished = proposal.status === "published";
  const isAccepted = proposal.status === "accepted";
  const selected = fields.find(f => f.id === selectedFieldId) || null;

  return (
    <div data-testid="signing-page-builder" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Toolbar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "12px 20px", borderBottom: "1px solid var(--pg-border)",
        background: "var(--pg-surface)",
      }}>
        <button className="pg-btn pg-btn-ghost" onClick={() => navigate("/proposals")} data-testid="sign-builder-back">
          <ArrowLeft size={14} /> Proposals
        </button>
        <div style={{ flex: 1, minWidth: 240 }}>
          <input
            value={proposal.name || ""}
            onChange={(e) => setProposal(p => ({ ...p, name: e.target.value }))}
            onBlur={async () => {
              try { await api.put(`/proposals/${id}`, { name: proposal.name }); }
              catch { /* ignore */ }
            }}
            className="pg-input"
            style={{ fontWeight: 600, fontSize: 15, maxWidth: 480 }}
            data-testid="sign-builder-name"
            disabled={isAccepted}
          />
          <div className="mono text-[11px] mt-1" style={{ color: "var(--pg-text-mute)" }}>
            {proposal.number} · {pages.length} page{pages.length === 1 ? "" : "s"} · {signers.length} signer{signers.length === 1 ? "" : "s"} · {workflow}
          </div>
        </div>
        <span style={{
          padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 500,
          background: isAccepted ? "rgba(22,163,74,0.14)" : isPublished ? "rgba(91,79,232,0.14)" : "rgba(100,116,139,0.14)",
          color: isAccepted ? "#16a34a" : isPublished ? "var(--pg-accent)" : "#64748b",
        }} data-testid="sign-builder-status">{proposal.status}</span>
        <button className="pg-btn" onClick={saveAll} disabled={saving || isAccepted || !anyDirty} data-testid="sign-builder-save">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {saving ? "Saving…" : (anyDirty ? "Save" : "Saved")}
        </button>
        {!isPublished && !isAccepted && (
          <button className="pg-btn pg-btn-primary" onClick={publish} data-testid="sign-builder-publish">
            <Eye size={13} /> Publish &amp; share
          </button>
        )}
        {(isPublished || isAccepted) && (
          <button className="pg-btn" onClick={() => openActivity()} data-testid="sign-builder-activity">
            <Activity size={13} /> Activity
          </button>
        )}
        {isPublished && (
          <>
            <button className="pg-btn" onClick={resendInvites} data-testid="sign-builder-resend-invites">
              <Mail size={13} /> Resend invites
            </button>
            <button className="pg-btn pg-btn-ghost" onClick={unpublish} data-testid="sign-builder-unpublish">
              <EyeOff size={13} /> Unpublish
            </button>
          </>
        )}
        {isAccepted && proposal.signed_pdf_file_id && (
          <a className="pg-btn pg-btn-primary" href={`${BACKEND}/api/public/files/${proposal.signed_pdf_file_id}`} target="_blank" rel="noreferrer" data-testid="sign-builder-download-signed">
            <Download size={13} /> Download signed PDF
          </a>
        )}
      </div>

      {/* 3-column body */}
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "220px 1fr 320px", minHeight: 0 }}>
        {/* Left rail — pages */}
        <aside style={{
          borderRight: "1px solid var(--pg-border)",
          background: "var(--pg-surface)",
          overflowY: "auto",
          padding: "14px 12px",
        }} data-testid="sign-builder-pages-rail">
          <div className="flex items-center justify-between mb-2">
            <h4 className="mono text-[11px] uppercase" style={{ color: "var(--pg-text-mute)", letterSpacing: ".08em" }}>Pages</h4>
            <span className="text-[11px]" style={{ color: "var(--pg-text-mute)" }}>{pages.length}</span>
          </div>
          <PageInserter onInsert={(type) => insertPage(-1, type)} testid="page-insert-top" />
          {pages.map((p, i) => (
            <div key={p.id}>
              <PageTile
                page={p}
                index={i}
                selected={activePageId === p.id}
                onSelect={() => { setActivePageId(p.id); setSelectedFieldId(null); }}
                onDelete={() => deletePage(p.id)}
                onDuplicate={() => duplicatePage(p.id)}
                onDragStart={(e) => onPageDragStart(e, p.id)}
                onDragOver={onPageDragOver}
                onDrop={(e) => onPageDrop(e, p.id)}
                fieldsOnPage={fields.filter(f => f.page_id === p.id).length}
                disabled={isAccepted}
              />
              <PageInserter onInsert={(type) => insertPage(i, type)} testid={`page-insert-after-${i}`} />
            </div>
          ))}
        </aside>

        {/* Center — active page canvas */}
        <main style={{ overflowY: "auto", padding: "20px 24px 60px", background: "var(--pg-bg)" }}>
          {activePage ? (
            <ActivePageCanvas
              page={activePage}
              fields={fields.filter(f => f.page_id === activePage.id)}
              signers={signers}
              activeSignerId={activeSignerId}
              activeSignerColor={activeColor}
              selectedFieldId={selectedFieldId}
              onSelectField={setSelectedFieldId}
              canvasRef={canvasRef}
              onFieldPointerDown={onFieldPointerDown}
              onCanvasDragOver={onCanvasDragOver}
              onCanvasDrop={onCanvasDrop}
              onVideoChange={(video) => {
                setPages(ps => ps.map(pp => pp.id === activePage.id ? { ...pp, video } : pp));
                markDirty("pages");
              }}
              onLabelChange={(label) => {
                setPages(ps => ps.map(pp => pp.id === activePage.id ? { ...pp, label } : pp));
                markDirty("pages");
              }}
              readOnly={isAccepted}
            />
          ) : (
            <div className="text-center text-sm mt-20" style={{ color: "var(--pg-text-mute)" }}>Add a page to get started.</div>
          )}
        </main>

        {/* Right rail — fields + signers + inspector */}
        <aside style={{
          borderLeft: "1px solid var(--pg-border)", padding: 16, overflowY: "auto",
          background: "var(--pg-surface)",
        }}>
          {/* Fields palette */}
          <h4 className="mono text-[11px] uppercase mb-2" style={{ color: "var(--pg-text-mute)", letterSpacing: ".08em" }}>
            Drag fields {activePage?.type === "video" ? "(disabled on video)" : "to the page"}
          </h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18 }}>
            {FIELD_PALETTE.map(p => {
              const Icon = p.icon;
              const disabled = isAccepted || activePage?.type === "video" || !activeSigner;
              return (
                <button
                  key={p.type}
                  type="button"
                  draggable={!disabled}
                  onDragStart={(e) => onPaletteDragStart(e, p.type)}
                  onClick={() => !disabled && addField(p.type)}
                  disabled={disabled}
                  data-testid={`sign-builder-add-${p.type}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "9px 10px", borderRadius: 8,
                    background: "var(--pg-surface-2)", border: "1px solid var(--pg-border)",
                    cursor: disabled ? "not-allowed" : "grab", fontSize: 12,
                    color: "var(--pg-text)", textAlign: "left",
                    opacity: disabled ? 0.5 : 1,
                  }}
                >
                  <Icon size={13} style={{ color: activeColor.chip }} />
                  <span>{p.label}</span>
                </button>
              );
            })}
          </div>

          {/* Signers */}
          <SignerManager
            signers={signers}
            activeSignerId={activeSignerId}
            onPick={setActiveSignerId}
            onAdd={addSigner}
            onUpdate={updateSigner}
            onRemove={removeSigner}
            onMove={moveSigner}
            workflow={workflow}
            onWorkflowChange={(w) => { setWorkflow(w); markDirty("signers"); }}
            fieldsBySigner={fieldsBySigner}
            onCopy={copyShareLink}
            onViewActivity={openActivity}
            isPublished={isPublished}
            readOnly={isAccepted}
          />

          {/* Field inspector */}
          {selected && (
            <div style={{ marginTop: 18, padding: 12, borderRadius: 10, border: "1px solid var(--pg-border)" }} data-testid="sign-builder-inspector">
              <h4 className="mono text-[11px] uppercase mb-2" style={{ color: "var(--pg-text-mute)", letterSpacing: ".08em" }}>Field</h4>
              <label className="block mb-1 text-[11px]" style={{ color: "var(--pg-text-mute)" }}>Label</label>
              <input className="pg-input w-full" value={selected.label || ""} onChange={(e) => updateField(selected.id, { label: e.target.value })} data-testid="sign-builder-inspector-label" disabled={isAccepted} />
              <label className="block mb-1 mt-3 text-[11px]" style={{ color: "var(--pg-text-mute)" }}>Assignee</label>
              <select
                className="pg-input w-full"
                value={selected.signer_id || ""}
                onChange={(e) => updateField(selected.id, { signer_id: e.target.value })}
                data-testid="sign-builder-inspector-signer"
                disabled={isAccepted}
              >
                {signers.map((s, i) => (
                  <option key={s.id} value={s.id}>{s.label}{s.name ? ` · ${s.name}` : ""}</option>
                ))}
              </select>
              {selected.type === "text" && (
                <>
                  <label className="block mb-1 mt-3 text-[11px]" style={{ color: "var(--pg-text-mute)" }}>Static text</label>
                  <textarea className="pg-input w-full" rows={3} value={selected.value || ""} onChange={(e) => updateField(selected.id, { value: e.target.value })} data-testid="sign-builder-inspector-value" disabled={isAccepted} />
                </>
              )}
              {selected.type !== "text" && selected.type !== "signature" && (
                <label className="flex items-center gap-2 mt-3 text-xs" style={{ cursor: "pointer" }}>
                  <input type="checkbox" checked={!!selected.required} onChange={(e) => updateField(selected.id, { required: e.target.checked })} data-testid="sign-builder-inspector-required" disabled={isAccepted} />
                  <span>Required</span>
                </label>
              )}
              <div className="flex gap-2 mt-3">
                <button className="pg-btn" onClick={() => duplicateField(selected.id)} style={{ fontSize: 12 }} disabled={isAccepted}><Copy size={12} /> Duplicate</button>
                <button className="pg-btn" onClick={() => deleteField(selected.id)} style={{ fontSize: 12, color: "var(--pg-red)" }} disabled={isAccepted}><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          )}
        </aside>
      </div>

      {activityOpen && (
        <SignerActivityDrawer
          activity={activity}
          loading={activityLoading}
          focusSignerId={activityFocusId}
          onClose={() => setActivityOpen(false)}
          onRefresh={() => openActivity(activityFocusId)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Left rail — page tile + inserter
// ---------------------------------------------------------------------------
function PageTile({ page, index, selected, onSelect, onDelete, onDuplicate, onDragStart, onDragOver, onDrop, fieldsOnPage, disabled }) {
  const Icon = page.type === "video" ? VideoIcon : page.type === "signing" ? PenLine : FileText;
  const previewUrl = page.type === "pdf_page" && page.source_meta?.preview_file_id
    ? `${BACKEND}/api/public/files/${page.source_meta.preview_file_id}` : null;
  return (
    <div
      draggable={!disabled}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={onSelect}
      data-testid={`page-tile-${index}`}
      style={{
        marginBottom: 8, borderRadius: 8, overflow: "hidden",
        background: "var(--pg-surface-2)",
        border: selected ? "2px solid var(--pg-accent)" : "1px solid var(--pg-border)",
        cursor: "pointer",
      }}
    >
      <div style={{ position: "relative", aspectRatio: "0.77 / 1", background: page.type === "video" ? (page.video?.bg_color || "#5B4FE8") : "white" }}>
        {previewUrl ? (
          <img src={previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, color: page.type === "video" ? "white" : "var(--pg-text-mute)" }}>
            <Icon size={22} />
            <div className="text-[11px]" style={{ fontWeight: 600 }}>
              {page.type === "video" ? "Video" : page.type === "signing" ? "Signing" : `Page ${(page.source_index ?? 0) + 1}`}
            </div>
          </div>
        )}
        {fieldsOnPage > 0 && (
          <span style={{
            position: "absolute", top: 4, right: 4,
            padding: "2px 6px", borderRadius: 999,
            background: "var(--pg-accent)", color: "white",
            fontSize: 10, fontWeight: 600,
          }}>{fieldsOnPage}</span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 8px" }}>
        <span className="text-[11px]" style={{ color: "var(--pg-text-mute)" }}>
          <GripVertical size={10} style={{ display: "inline", opacity: 0.6, marginRight: 2 }} />
          #{index + 1}
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={(e) => { e.stopPropagation(); onDuplicate(); }} className="pg-btn pg-btn-ghost" style={{ padding: 2 }} aria-label="Duplicate page" title="Duplicate" disabled={disabled} data-testid={`page-tile-${index}-dup`}><Copy size={11} /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="pg-btn pg-btn-ghost" style={{ padding: 2, color: "var(--pg-red)" }} aria-label="Delete page" title="Delete" disabled={disabled} data-testid={`page-tile-${index}-del`}><Trash2 size={11} /></button>
        </div>
      </div>
    </div>
  );
}

function PageInserter({ onInsert, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      style={{ position: "relative", height: open ? 46 : 16, transition: "height 160ms", marginBottom: open ? 6 : 0 }}
      data-testid={testid}
    >
      <div style={{ height: 1, background: open ? "var(--pg-accent)" : "transparent", position: "absolute", top: "50%", left: 0, right: 0 }} />
      {open ? (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0,
          display: "flex", gap: 6, alignItems: "center", justifyContent: "center",
        }}>
          <button
            onClick={() => onInsert("signing")}
            className="pg-btn pg-btn-primary"
            data-testid={`${testid}-signing`}
            style={{ padding: "4px 8px", fontSize: 11 }}
          ><PenLine size={11} /> Signing</button>
          <button
            onClick={() => onInsert("video")}
            className="pg-btn"
            data-testid={`${testid}-video`}
            style={{ padding: "4px 8px", fontSize: 11 }}
          ><VideoIcon size={11} /> Video</button>
        </div>
      ) : (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--pg-text-mute)", fontSize: 10, opacity: 0.5,
        }}>
          <Plus size={11} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Center canvas
// ---------------------------------------------------------------------------
function ActivePageCanvas({
  page, fields, signers, activeSignerId, activeSignerColor,
  selectedFieldId, onSelectField, canvasRef, onFieldPointerDown,
  onCanvasDragOver, onCanvasDrop, onVideoChange, onLabelChange, readOnly,
}) {
  if (page.type === "video") {
    return (
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <h3 className="text-sm mb-3" style={{ fontWeight: 600 }}>Video page</h3>
        <VideoBlockEditor
          value={page.video || {}}
          onChange={onVideoChange}
          testIdPrefix="sign-builder-video"
        />
      </div>
    );
  }

  // Determine aspect ratio
  let aspect = 0.77;  // US Letter ratio ≈ 8.5/11
  if (page.type === "pdf_page") {
    const w = page.source_meta?.width || 612;
    const h = page.source_meta?.height || 792;
    aspect = w / h;
  }
  const bgImage = page.type === "pdf_page" && page.source_meta?.preview_file_id
    ? `url("${BACKEND}/api/public/files/${page.source_meta.preview_file_id}")` : null;

  const sigIdxById = Object.fromEntries(signers.map((s, i) => [s.id, i]));

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm" style={{ fontWeight: 600 }}>
          {page.type === "signing" ? (
            <input
              className="pg-input"
              value={page.label || ""}
              onChange={(e) => onLabelChange(e.target.value)}
              style={{ fontSize: 13, fontWeight: 600, maxWidth: 300 }}
              data-testid="sign-builder-signing-label"
              disabled={readOnly}
            />
          ) : page.type === "pdf_page" ? `Document page ${(page.source_index ?? 0) + 1}` : "Page"}
        </h3>
        <span className="text-xs" style={{ color: "var(--pg-text-mute)" }}>
          Active signer: <strong style={{ color: activeSignerColor.chip }}>
            {signers.find(s => s.id === activeSignerId)?.label || "—"}
          </strong>
        </span>
      </div>
      <div
        ref={canvasRef}
        onDragOver={onCanvasDragOver}
        onDrop={onCanvasDrop}
        onClick={() => onSelectField(null)}
        data-testid="sign-builder-canvas"
        style={{
          position: "relative", width: "100%", aspectRatio: `${aspect}`,
          background: page.type === "signing" ? "white" : "white",
          backgroundImage: bgImage,
          backgroundSize: "100% 100%",
          backgroundRepeat: "no-repeat",
          borderRadius: 8, border: "1px solid var(--pg-border)",
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          overflow: "hidden", userSelect: "none",
        }}
      >
        {page.type === "signing" && (
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: "5%",
            background: "var(--pg-accent)", color: "white",
            padding: "0 16px", display: "flex", alignItems: "center",
            fontSize: 11, fontWeight: 600,
          }}>
            {page.label || "Signatures & Client Details"}
          </div>
        )}

        {fields.map((f) => {
          const idx = sigIdxById[f.signer_id] ?? 0;
          const col = SIGNER_COLORS[idx % SIGNER_COLORS.length];
          const PaletteIcon = PALETTE_BY_TYPE[f.type]?.icon || Type;
          const selected = selectedFieldId === f.id;
          return (
            <div
              key={f.id}
              onPointerDown={readOnly ? undefined : (e) => onFieldPointerDown(e, f.id, "move")}
              onClick={(e) => { e.stopPropagation(); onSelectField(f.id); }}
              data-testid={`sign-field-${f.id}`}
              style={{
                position: "absolute",
                left: `${f.x * 100}%`, top: `${f.y * 100}%`,
                width: `${f.width * 100}%`, height: `${f.height * 100}%`,
                border: `${selected ? 2 : 1}px dashed ${col.stroke}`,
                background: col.fill,
                borderRadius: 4,
                padding: "3px 6px",
                cursor: readOnly ? "default" : "move",
                display: "flex", flexDirection: "column", justifyContent: "center",
                overflow: "hidden", boxSizing: "border-box",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color: col.chip, letterSpacing: ".04em", textTransform: "uppercase", fontWeight: 700, opacity: 0.85 }}>
                <PaletteIcon size={9} />
                <span>{f.label || f.type}</span>
                {f.required && <span style={{ color: "var(--pg-red)" }}>*</span>}
              </div>
              {f.type === "text" && (
                <div style={{ marginTop: 2, fontSize: 10, color: "var(--pg-text)", overflow: "hidden", whiteSpace: "pre-wrap" }}>
                  {f.value || "Add text in the field panel"}
                </div>
              )}
              {!readOnly && selected && (
                <div
                  onPointerDown={(e) => onFieldPointerDown(e, f.id, "resize")}
                  style={{
                    position: "absolute", right: -4, bottom: -4,
                    width: 10, height: 10, borderRadius: 2,
                    background: col.stroke, border: "2px solid white",
                    cursor: "nwse-resize",
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right-rail signer manager
// ---------------------------------------------------------------------------
function SignerManager({ signers, activeSignerId, onPick, onAdd, onUpdate, onRemove, onMove, workflow, onWorkflowChange, fieldsBySigner, onCopy, onViewActivity, isPublished, readOnly }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div style={{ borderTop: "1px solid var(--pg-border)", paddingTop: 14, marginTop: 4 }}>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", background: "transparent", border: "none", padding: 0, cursor: "pointer", color: "var(--pg-text)" }}
        data-testid="sign-builder-signers-toggle"
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <h4 className="mono text-[11px] uppercase" style={{ color: "var(--pg-text-mute)", letterSpacing: ".08em", margin: 0 }}>Signers</h4>
          <span className="text-[11px]" style={{ color: "var(--pg-text-mute)" }}>({signers.length})</span>
        </span>
        {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
      </button>
      {expanded && (
        <>
          <div className="flex gap-2 mt-2 mb-3">
            <SegBtn active={workflow === "sequential"} onClick={() => onWorkflowChange("sequential")} testid="sign-builder-wf-sequential" disabled={readOnly}>Sequential</SegBtn>
            <SegBtn active={workflow === "parallel"}   onClick={() => onWorkflowChange("parallel")}   testid="sign-builder-wf-parallel"   disabled={readOnly}>Parallel</SegBtn>
          </div>
          <p className="text-[11px] mb-3" style={{ color: "var(--pg-text-mute)" }}>
            {workflow === "sequential"
              ? "Signer 1 signs first; the next signer only gets their link once the previous one is done."
              : "All signers can sign simultaneously; the proposal finalises once everyone has signed."}
          </p>
          {signers.map((s, i) => {
            const col = colorForSigner(i);
            const active = activeSignerId === s.id;
            const signed = s.status === "signed";
            return (
              <div
                key={s.id}
                onClick={() => onPick(s.id)}
                data-testid={`signer-${i}`}
                style={{
                  padding: 10, marginBottom: 8, borderRadius: 8,
                  border: active ? `2px solid ${col.chip}` : "1px solid var(--pg-border)",
                  background: active ? col.fill : "var(--pg-surface)",
                  cursor: "pointer",
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 10, height: 10, borderRadius: 999, background: col.chip, flexShrink: 0 }} />
                  <strong style={{ fontSize: 12, color: col.chip }}>{s.label}</strong>
                  <span className="text-[11px]" style={{ color: "var(--pg-text-mute)", flex: 1 }}>· {fieldsBySigner[s.id] || 0} field{(fieldsBySigner[s.id] || 0) === 1 ? "" : "s"}</span>
                  {signed && (
                    <span className="mono text-[10px]" style={{ padding: "1px 6px", borderRadius: 4, background: "rgba(22,163,74,0.14)", color: "#16a34a" }}>signed</span>
                  )}
                </div>
                <input
                  className="pg-input w-full"
                  value={s.name || ""}
                  placeholder="Name (optional)"
                  onChange={(e) => onUpdate(s.id, { name: e.target.value })}
                  data-testid={`signer-${i}-name`}
                  style={{ fontSize: 12, padding: "5px 8px", marginBottom: 4 }}
                  disabled={readOnly}
                />
                <input
                  className="pg-input w-full"
                  value={s.email || ""}
                  placeholder="Email"
                  onChange={(e) => onUpdate(s.id, { email: e.target.value })}
                  data-testid={`signer-${i}-email`}
                  style={{ fontSize: 12, padding: "5px 8px" }}
                  type="email"
                  disabled={readOnly}
                />
                <div className="flex items-center gap-1 mt-2">
                  {isPublished && s.token && (
                    <button className="pg-btn" onClick={(e) => { e.stopPropagation(); onCopy(s); }} style={{ fontSize: 11, padding: "3px 7px" }} data-testid={`signer-${i}-copy-link`}>
                      <Link2 size={11} /> Copy link
                    </button>
                  )}
                  {isPublished && onViewActivity && (
                    <button className="pg-btn" onClick={(e) => { e.stopPropagation(); onViewActivity(s.id); }} style={{ fontSize: 11, padding: "3px 7px" }} data-testid={`signer-${i}-activity`} title="View signer activity">
                      <Activity size={11} />
                    </button>
                  )}
                  {!readOnly && (
                    <>
                      <button className="pg-btn pg-btn-ghost" onClick={(e) => { e.stopPropagation(); onMove(s.id, -1); }} style={{ fontSize: 11, padding: "3px 7px" }} aria-label="Move up" disabled={i === 0} data-testid={`signer-${i}-up`}>↑</button>
                      <button className="pg-btn pg-btn-ghost" onClick={(e) => { e.stopPropagation(); onMove(s.id, +1); }} style={{ fontSize: 11, padding: "3px 7px" }} aria-label="Move down" disabled={i === signers.length - 1} data-testid={`signer-${i}-down`}>↓</button>
                      <div style={{ flex: 1 }} />
                      <button className="pg-btn pg-btn-ghost" onClick={(e) => { e.stopPropagation(); onRemove(s.id); }} style={{ fontSize: 11, padding: "3px 7px", color: "var(--pg-red)" }} aria-label="Remove signer" data-testid={`signer-${i}-remove`}><Trash2 size={11} /></button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {!readOnly && (
            <button className="pg-btn w-full" onClick={onAdd} data-testid="sign-builder-add-signer" style={{ fontSize: 12, marginTop: 4 }}>
              <UserPlus size={13} /> Add signer
            </button>
          )}
        </>
      )}
    </div>
  );
}

function SegBtn({ active, onClick, testid, children, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      style={{
        padding: "4px 10px", borderRadius: 999, border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 11, fontWeight: active ? 600 : 400,
        background: active ? "var(--pg-accent)" : "var(--pg-surface-2)",
        color: active ? "white" : "var(--pg-text-mute)",
        opacity: disabled ? 0.6 : 1,
      }}
    >{children}</button>
  );
}

// ---------------------------------------------------------------------------
// Signer activity drawer — right-side overlay showing a per-signer timeline
// of invite emails, page opens, signatures, and declines. Reps use this to
// diagnose "stuck" signers.
// ---------------------------------------------------------------------------
const EVENT_META = {
  invite_sent:    { label: "Invite emailed",     Icon: Send,         color: "#5B4FE8" },
  invite_failed:  { label: "Invite failed",      Icon: AlertCircle,  color: "#dc2626" },
  invite_skipped: { label: "Invite skipped",     Icon: AlertCircle,  color: "#64748b" },
  viewed:         { label: "Opened the proposal", Icon: Eye,         color: "#0EA5A4" },
  signed:         { label: "Signed",              Icon: CheckCircle2, color: "#16a34a" },
  declined:       { label: "Declined",            Icon: Ban,          color: "#dc2626" },
};

function fmtTs(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    const today = new Date();
    const same = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    if (same) return `Today · ${time}`;
    return `${d.toLocaleDateString(undefined, { day: "2-digit", month: "short" })} · ${time}`;
  } catch { return ts; }
}

function SignerActivityDrawer({ activity, loading, focusSignerId, onClose, onRefresh }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(15,23,42,0.5)", display: "flex", justifyContent: "flex-end" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="signer-activity-drawer"
    >
      <aside
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: 460, maxWidth: "95vw", height: "100%",
          background: "var(--pg-surface)", borderLeft: "1px solid var(--pg-border)",
          display: "flex", flexDirection: "column",
          boxShadow: "-24px 0 60px rgba(0,0,0,0.22)",
        }}
      >
        <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--pg-border)" }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Signer activity</h3>
            <p className="text-xs mt-1" style={{ color: "var(--pg-text-mute)", margin: 0 }}>
              Email dispatches, opens, signatures, and declines — in order.
            </p>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="pg-btn pg-btn-ghost" style={{ padding: 6 }} onClick={onRefresh} aria-label="Refresh" title="Refresh" data-testid="signer-activity-refresh">
              <Loader2 size={13} className={loading ? "animate-spin" : ""} />
            </button>
            <button className="pg-btn pg-btn-ghost" style={{ padding: 6 }} onClick={onClose} aria-label="Close" data-testid="signer-activity-close"><X size={14} /></button>
          </div>
        </header>
        <div style={{ flex: 1, overflowY: "auto", padding: "14px 20px 28px" }}>
          {loading && !activity ? (
            <div className="text-center text-sm mt-12" style={{ color: "var(--pg-text-mute)" }}>
              <Loader2 size={18} className="animate-spin" style={{ display: "inline-block" }} /> Loading activity…
            </div>
          ) : activity && activity.signers ? (
            activity.signers.length === 0 ? (
              <div className="text-sm" style={{ color: "var(--pg-text-mute)" }}>No signers yet.</div>
            ) : (
              activity.signers.map((s, i) => {
                const col = colorForSigner(i);
                const isFocused = focusSignerId === s.id;
                const StatusChip = {
                  pending: { label: "pending", bg: "rgba(100,116,139,0.14)", color: "#64748b", Icon: Clock },
                  signed:  { label: "signed",  bg: "rgba(22,163,74,0.14)",   color: "#16a34a", Icon: CheckCircle2 },
                  declined:{ label: "declined", bg: "rgba(220,38,38,0.14)",   color: "#dc2626", Icon: Ban },
                }[s.status] || { label: s.status, bg: "rgba(100,116,139,0.14)", color: "#64748b", Icon: Clock };
                const SIcon = StatusChip.Icon;
                return (
                  <section
                    key={s.id}
                    data-testid={`activity-signer-${s.id}`}
                    style={{
                      marginBottom: 18,
                      padding: 12,
                      borderRadius: 10,
                      border: isFocused ? `2px solid ${col.chip}` : "1px solid var(--pg-border)",
                      background: isFocused ? col.fill : "var(--pg-surface-2)",
                    }}
                  >
                    <header style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 999, background: col.chip, flexShrink: 0 }} />
                      <strong style={{ fontSize: 13, color: col.chip }}>{s.label}</strong>
                      {s.name && <span className="text-xs" style={{ color: "var(--pg-text-mute)" }}>· {s.name}</span>}
                      <span style={{ flex: 1 }} />
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, background: StatusChip.bg, color: StatusChip.color, fontSize: 10, fontWeight: 600 }}>
                        <SIcon size={10} /> {StatusChip.label}
                      </span>
                    </header>
                    <div className="text-xs" style={{ color: "var(--pg-text-mute)", marginBottom: 10 }}>
                      {s.email || "No email set"}
                    </div>
                    {s.decline_reason && (
                      <div
                        style={{
                          marginBottom: 10, padding: "8px 10px",
                          background: "rgba(220,38,38,0.08)",
                          border: "1px solid rgba(220,38,38,0.3)",
                          borderRadius: 6, fontSize: 12, color: "#dc2626",
                        }}
                      >
                        <strong>Reason:</strong> {s.decline_reason}
                      </div>
                    )}
                    {s.events.length === 0 ? (
                      <div className="text-xs" style={{ color: "var(--pg-text-mute)" }}>
                        No activity yet — invites will show up here once this signer is notified.
                      </div>
                    ) : (
                      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                        {s.events.map((ev, idx) => {
                          const meta = EVENT_META[ev.type] || { label: ev.type, Icon: Activity, color: "#64748b" };
                          const EIcon = meta.Icon;
                          return (
                            <li key={idx} style={{ display: "flex", gap: 10, padding: "6px 0", borderTop: idx === 0 ? "none" : "1px dashed var(--pg-border)" }}>
                              <div style={{
                                width: 24, height: 24, borderRadius: 999,
                                background: `${meta.color}22`, color: meta.color,
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                              }}>
                                <EIcon size={12} />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, color: "var(--pg-text)", fontWeight: 500 }}>
                                  {meta.label}
                                </div>
                                {ev.note && (
                                  <div className="text-xs truncate" style={{ color: "var(--pg-text-mute)" }}>{ev.note}</div>
                                )}
                                <div className="mono text-[10px] mt-0.5" style={{ color: "var(--pg-text-mute)" }}>{fmtTs(ev.ts)}</div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>
                );
              })
            )
          ) : (
            <div className="text-sm" style={{ color: "var(--pg-text-mute)" }}>Could not load activity.</div>
          )}
        </div>
      </aside>
    </div>
  );
}

